import BackgroundColors from '@root/joinroot.com/src/utils/background-colors';
import ImageToutSideBySide, { IMAGE_HORIZONTAL_ALIGNMENTS, IMAGE_SIZES, IMAGE_VERTICAL_ALIGNMENTS } from '@root/joinroot.com/src/components/sections/image-tout/image-tout-side-by-side';
import ImageToutSideBySideSection from '@root/joinroot.com/src/components/sections/image-tout/image-tout-side-by-side-section';
import LinkButton from '@root/joinroot.com/src/models/link-button';
import PropTypes from '@root/vendor/prop-types';
import renderRichText from '@root/joinroot.com/src/utils/render-rich-text';
import useAnalytics from '@root/core/src/hooks/use-analytics';
import { BRAND_BLING_OPTIONS } from '@root/joinroot.com/src/components/brand-bling';
import { constantize } from '@root/core/src/utils/strings';

export default function ImageToutSideBySideSectionBlock({ content }) {
  const { trackClick } = useAnalytics(`IMAGE_TOUT_SIDE_BY_SIDE_SECTION_${content.id}`, false);

  return (
    <div data-testid={'imageToutSideBySideBlock'}>
      <ImageToutSideBySideSection
        anchor={content.anchor?.anchorSlug}
        backgroundColor={BackgroundColors.getDefaultedString(content.backgroundColor)}
        body={renderRichText(content.body)}
        eyebrow={content.eyebrow}
        title={content.headline}
      >
        {content.sections.map((sideBySide) => {
          const { imageTout } = sideBySide;
          return (
            <ImageToutSideBySide
              backgroundColor={BackgroundColors.getDefaultedString(sideBySide.backgroundColor)}
              body={renderRichText(imageTout.body)}
              brandBlingOptions={buildImageOverlay(sideBySide.imageOverlay)}
              button={buildButton(imageTout.callToActionButton)}
              eyebrow={imageTout.eyebrow}
              image={imageTout.asset}
              imageAltText={imageTout.imageAltText}
              imageFullBleed={isImageFullBleed(sideBySide.imageSize)}
              imageHorizontalAlignment={buildImageHorizontalAlignment(sideBySide.imageAlignment)}
              imageSize={buildImageSize(sideBySide.imageSize)}
              imageVerticalAlignment={buildImageVerticalAlignment(sideBySide.verticalImageAlignment)}
              key={sideBySide.id}
              title={imageTout.headline}
              trackClick={trackClick}
            />
          );
        })
        }
      </ImageToutSideBySideSection>
    </div>
  );
}

ImageToutSideBySideSectionBlock.propTypes = {
  content: PropTypes.shape({
    anchor: PropTypes.shape({
      id: PropTypes.string,
      anchorSlug: PropTypes.string,
    }),
    backgroundColor: PropTypes.string.isRequired,
    eyebrow: PropTypes.string,
    id: PropTypes.string.isRequired,
    headline: PropTypes.string,
    body: PropTypes.string,
    sections: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        body: PropTypes.object,
        eyebrow: PropTypes.string,
        headline: PropTypes.string,
        imageAlignment: PropTypes.string.isRequired,
        verticalImageAlignment: PropTypes.string,
        imageSize: PropTypes.string,
        asset: PropTypes.shape({
          file: PropTypes.shape({
            url: PropTypes.string.isRequired,
          }).isRequired,
        }).isRequired,
        imageAltText: PropTypes.string,
        callToActionButton: PropTypes.shape({
          id: PropTypes.string.isRequired,
          buttonType: PropTypes.oneOf(Object.keys(LinkButton.types)).isRequired,
          buttonText: PropTypes.string.isRequired,
          buttonLink: PropTypes.string.isRequired,
        }),
      }).isRequired,
    ).isRequired,
  }).isRequired,
};

function isImageFullBleed(imageSize) {
  if (!imageSize) { return false; }

  return imageSize.toLowerCase().includes('full-bleed');
}

function buildImageSize(imageSize) {
  if (isImageFullBleed(imageSize)) { return null; }

  return imageSize.toLowerCase() === 'small' ? IMAGE_SIZES.SMALL : IMAGE_SIZES.MEDIUM;
}

function buildImageHorizontalAlignment(imageAlignment) {
  return imageAlignment.toLowerCase().includes('left') ? IMAGE_HORIZONTAL_ALIGNMENTS.LEFT : IMAGE_HORIZONTAL_ALIGNMENTS.RIGHT;
}

function buildImageVerticalAlignment(verticalImageAlignment) {
  if (!verticalImageAlignment) { return IMAGE_VERTICAL_ALIGNMENTS.CENTER; }

  return verticalImageAlignment.toLowerCase().includes('top') ? IMAGE_VERTICAL_ALIGNMENTS.TOP : IMAGE_VERTICAL_ALIGNMENTS.CENTER;
}

function buildImageOverlay(imageOverlay) {
  const IMAGE_OVERLAY = {
    NONE: BRAND_BLING_OPTIONS.NONE,
    CARET: BRAND_BLING_OPTIONS.CARET,
    LARGE_ROOT_TEXT: BRAND_BLING_OPTIONS.WORDMARK,
  };

  return IMAGE_OVERLAY[constantize(imageOverlay)] || BRAND_BLING_OPTIONS.NONE;
}

function buildButton(button) {
  if (!button) { return; }

  return {
    id: button.id,
    type: LinkButton.types[constantize(button.buttonType)],
    text: button.buttonText,
    link: button.buttonLink,
  };
}
